import React, { useEffect, useMemo } from 'react';
import { CallRecordKey } from 'flow/types/EntityKeys';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import IconButton from 'flow/components/ui-components/IconButton';
import Icon from 'flow/components/ui-components/Icon';
import Tooltip from 'flow/components/ui-components/Tooltip';
import ContactInfoCard from './ContactInfoCard';
import ContactTitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactTitle';
import ContactSubtitle from 'flow/components/pbx/v2/pbx-queue-items/history/call-record/ContactSubtitle';
import {
    getContactFromCallRecord,
    getContactFullName,
} from 'flow/utils/call-record-utils';
import useAppContext from 'flow/hooks/useAppContext';
import { getCallableNumberFromContact } from 'flow/utils/extension-utils';
import {
    fetchContact,
    fetchEditableContact,
} from 'flow/actions/contact-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    makeSelectExtensionKeyFromContactKey,
    selectUserCountryCode,
} from 'flow/selectors/ExtensionSelectors';
import { selectCallRecordFromKey } from 'flow/selectors/call-selectors';
import { getContact } from 'flow/selectors/contact-selectors';
import { isThirdPartyContactKey } from 'flow/utils';

interface SectionContactProps {
    callRecordKey: CallRecordKey;
}

const SectionContact = ({ callRecordKey }: SectionContactProps) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const ownCountryCode = useAppSelector(selectUserCountryCode);
    const callRecord = useAppSelector((state) =>
        selectCallRecordFromKey(state, callRecordKey),
    );
    const callRecordContact = callRecord?.get('contact');
    const contactKey = callRecordContact?.get('key');

    const selectExtensionKey = useMemo(
        makeSelectExtensionKeyFromContactKey,
        [],
    );
    const extensionKey = useAppSelector((state) =>
        contactKey ? selectExtensionKey(state, contactKey) : undefined,
    );

    const storeContact = useAppSelector((state) =>
        contactKey ? getContact(state, contactKey, extensionKey) : undefined,
    );

    useEffect(() => {
        if (contactKey) {
            void dispatch(fetchContact(contactKey));
            void dispatch(fetchEditableContact(contactKey));
        }
    }, [dispatch, contactKey]);

    if (!callRecord) return null;

    // Prefer store contact (updated after edits) over call record snapshot
    const contact = storeContact || callRecordContact;
    const contactFullName = getContactFullName(callRecord);
    const phoneNumber = callRecord.getIn(['number', 'number']) as string;
    const sourceLabel =
        contact?.get('sourceDescription') || contact?.get('contactSource');
    const callableNumber = contact
        ? getCallableNumberFromContact(contact)
        : phoneNumber;
    const contactIsSaved =
        contact?.has('key') && !isThirdPartyContactKey(contact.get('key'));

    return (
        <div className="flex flex-col gap-3">
            <div className="bg-gray-150 flex items-center gap-4 rounded-sm px-6 py-4 dark:border-none dark:bg-gray-100">
                <ContactAvatar contact={contact} size="large" />

                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2">
                        <span className="truncate text-xl font-semibold">
                            <ContactTitle
                                contact={contact}
                                contactFullName={contactFullName}
                                number={callRecord.get('number')}
                            />
                        </span>
                        {sourceLabel && (
                            <button className="hover:darken-10 inline-flex shrink-0 items-center gap-1 rounded-4xl bg-gray-300 px-2.5 py-0.5 text-xs text-gray-600">
                                {sourceLabel}
                                <Icon iconKey="arrow_down" size="1rem" />
                            </button>
                        )}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-500">
                        <ContactSubtitle
                            contact={contact}
                            contactFullName={contactFullName}
                            number={callRecord.get('number')}
                        />
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <CallAdaptiveButton
                        phoneNumber={callableNumber}
                        extensionKey={extensionKey}
                        size="medium"
                        activeBg={false}
                        inactiveBgColor="bg-gray-200"
                    />
                    {!contactIsSaved && (
                        <Tooltip
                            label={i18next.t(
                                'Colleagues.Create.External.Title',
                            )}
                        >
                            <IconButton
                                aria-label={i18next.t(
                                    'cti.wrapper.button.create.contact',
                                )}
                                onClick={() =>
                                    openModal('createContact', {
                                        contact:
                                            getContactFromCallRecord(
                                                callRecord,
                                            ),
                                        countryCode: ownCountryCode,
                                    })
                                }
                                className="hover:darken-10 size-10! rounded-sm bg-gray-200 p-0 text-gray-800"
                            >
                                <Icon iconKey="person_add" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>
            </div>
            {contactIsSaved && (
                <ContactInfoCard
                    contact={contact}
                    extensionKey={extensionKey}
                />
            )}
        </div>
    );
};

export default SectionContact;
